<script setup lang="ts">
  import { ref } from 'vue';
  import { ITable } from '@/interfaces/ITable';

  const props = defineProps<{
    tables: ITable[];
    /** Blocks new drags (e.g. while the layout is being saved). */
    disabled?: boolean;
  }>();

  const emit = defineEmits<{
    (
      e: 'drop-table',
      payload: { tableId: number; clientX: number; clientY: number }
    ): void;
    (e: 'new-table'): void;
  }>();

  interface ITrayDragState {
    tableId: number;
    pointerId: number;
    name: string;
    x: number;
    y: number;
    moved: boolean;
  }

  const trayDrag = ref<ITrayDragState | null>(null);

  function onChipPointerDown(event: PointerEvent, table: ITable) {
    if (event.button !== 0 || trayDrag.value || props.disabled) return;

    (event.currentTarget as HTMLElement).setPointerCapture(event.pointerId);

    trayDrag.value = {
      tableId: table.id,
      pointerId: event.pointerId,
      name: table.name,
      x: event.clientX,
      y: event.clientY,
      moved: false,
    };
  }

  function onChipPointerMove(event: PointerEvent) {
    const d = trayDrag.value;
    if (!d || d.pointerId !== event.pointerId) return;

    d.x = event.clientX;
    d.y = event.clientY;
    d.moved = true;
  }

  function onChipPointerUp(event: PointerEvent) {
    const d = trayDrag.value;
    if (!d || d.pointerId !== event.pointerId) return;

    trayDrag.value = null;

    if (!d.moved) return;

    emit('drop-table', {
      tableId: d.tableId,
      clientX: event.clientX,
      clientY: event.clientY,
    });
  }

  function onChipPointerCancel(event: PointerEvent) {
    const d = trayDrag.value;
    if (!d || d.pointerId !== event.pointerId) return;
    trayDrag.value = null;
  }
</script>

<template>
  <div class="floor-plan-unplaced-tray">
    <div class="tray-header">
      <div class="tray-header-title">
        <v-icon small>mdi-tray-full</v-icon>
        <span>{{ $t('floorPlan.unplaced.title') }}</span>
        <span v-if="tables.length" class="tray-header-count">
          {{ tables.length }}
        </span>
      </div>
      <v-btn small text color="primary" @click="emit('new-table')">
        <v-icon left small>mdi-plus</v-icon>
        <span>{{ $t('table.new') }}</span>
      </v-btn>
    </div>

    <div class="tray-body">
      <template v-if="tables.length">
        <div
          v-for="table in tables"
          :key="table.id"
          class="tray-chip"
          :class="{ 'is-dragging': trayDrag && trayDrag.tableId === table.id }"
          @pointerdown="onChipPointerDown($event, table)"
          @pointermove="onChipPointerMove"
          @pointerup="onChipPointerUp"
          @pointercancel="onChipPointerCancel"
        >
          <v-icon x-small class="tray-chip-icon">mdi-table-furniture</v-icon>
          <span class="tray-chip-name">{{ table.name }}</span>
        </div>
        <span class="tray-hint">{{ $t('floorPlan.unplaced.hint') }}</span>
      </template>
      <span v-else class="tray-empty">
        {{ $t('floorPlan.unplaced.empty') }}
      </span>
    </div>

    <div
      v-if="trayDrag && trayDrag.moved"
      class="tray-ghost"
      :style="{ left: `${trayDrag.x}px`, top: `${trayDrag.y}px` }"
    >
      {{ trayDrag.name }}
    </div>
  </div>
</template>

<style scoped lang="scss">
  .floor-plan-unplaced-tray {
    @apply tw-bg-white tw-border tw-border-gray-100 tw-rounded-xl tw-p-3;

    .tray-header {
      @apply tw-flex tw-items-center tw-justify-between tw-mb-2;

      .tray-header-title {
        @apply tw-flex tw-items-center tw-gap-1.5 tw-text-sm tw-font-semibold tw-text-gray-600;

        .tray-header-count {
          @apply tw-bg-gray-100 tw-text-gray-600 tw-text-xs tw-rounded-full tw-px-2 tw-py-0.5;
        }
      }
    }

    .tray-body {
      @apply tw-flex tw-flex-wrap tw-items-center tw-gap-2;

      .tray-chip {
        @apply tw-flex tw-items-center tw-gap-1.5 tw-px-3 tw-py-1.5 tw-rounded-lg tw-border tw-border-gray-200 tw-bg-gray-50 tw-cursor-grab tw-select-none;
        touch-action: none;

        &:hover {
          @apply tw-border-orange-300 tw-bg-orange-50;
        }

        &.is-dragging {
          @apply tw-opacity-40 tw-cursor-grabbing;
        }

        .tray-chip-icon {
          @apply tw-text-gray-500;
        }

        .tray-chip-name {
          @apply tw-text-sm tw-font-medium tw-text-gray-700;
        }
      }

      .tray-hint {
        @apply tw-text-xs tw-text-gray-400;
      }

      .tray-empty {
        @apply tw-text-sm tw-text-gray-400;
      }
    }

    .tray-ghost {
      @apply tw-fixed tw-z-50 tw-px-3 tw-py-1.5 tw-rounded-lg tw-border-2 tw-border-orange-400 tw-bg-orange-50 tw-text-sm tw-font-medium tw-text-gray-700 tw-shadow-lg tw-pointer-events-none;
      transform: translate(-50%, -50%);
    }
  }
</style>
